import {Button, CounterLabel} from '@primer/react'
import {Link} from '@github-ui/react-core/link'
import {DockedPanel, useDockedPanelActions, type PanelMetadata} from '../../../contexts/DockedPanelContext'
import {useRef} from 'react'
import {usePullRequestAnalytics} from '../../../hooks/use-pull-request-analytics'

type AnalyticsPayload = Record<string, unknown>
type SendAnalytics = (payload: AnalyticsPayload) => void

type ActionConfigBase = {
  label: string
  ariaLabel?: string
  analyticsPayload?: AnalyticsPayload
  /** Optional counter rendered next to the button label */
  count?: number
  /** Button visual variant (defaults to 'invisible') */
  variant?: 'invisible' | 'primary' | 'default' | 'danger'
}

export type ActionConfig =
  | (ActionConfigBase & {linkTo: string})
  | (ActionConfigBase & {onClick: () => void})
  | (ActionConfigBase & {
      panel: DockedPanel
      metadata?: PanelMetadata
    })

function ActionLabel({label, count}: {label: string; count?: number}) {
  if (count == null) return <>{label}</>
  return (
    <span className="d-inline-flex flex-items-center gap-2">
      {label}
      <CounterLabel>{count}</CounterLabel>
    </span>
  )
}

export function Action({action}: {action: ActionConfig}) {
  const {sendPullRequestAnalyticsEvent} = usePullRequestAnalytics()
  const sendAnalytics: SendAnalytics = payload => {
    sendPullRequestAnalyticsEvent('overview.action_item_click', 'OVERVIEW_ACTION_ITEM', payload)
  }

  if ('linkTo' in action) {
    return <LinkAction action={action} sendAnalytics={sendAnalytics} />
  }

  if ('panel' in action) {
    return <DockedPanelAction action={action} sendAnalytics={sendAnalytics} />
  }

  return (
    <Button
      type="button"
      variant={action.variant ?? 'invisible'}
      size="small"
      aria-label={action.ariaLabel}
      onClick={() => {
        sendAnalytics({action_type: 'click', label: action.label, ...action.analyticsPayload})
        action.onClick()
      }}
    >
      <ActionLabel label={action.label} count={action.count} />
    </Button>
  )
}

// Link actions navigate to a different route. Close any open docked panel
function LinkAction({
  action,
  sendAnalytics,
}: {
  action: Extract<ActionConfig, {linkTo: string}>
  sendAnalytics: SendAnalytics
}) {
  const {closePanel} = useDockedPanelActions()
  return (
    <Button
      as={Link}
      to={action.linkTo}
      variant={action.variant ?? 'invisible'}
      size="small"
      aria-label={action.ariaLabel}
      onClick={() => {
        sendAnalytics({action_type: 'link', label: action.label, ...action.analyticsPayload})
        closePanel()
      }}
    >
      <ActionLabel label={action.label} count={action.count} />
    </Button>
  )
}

// Separate component for panel actions to manage focus return after closing the panel
function DockedPanelAction({
  action,
  sendAnalytics,
}: {
  action: Extract<ActionConfig, {panel: string}>
  sendAnalytics: SendAnalytics
}) {
  const {openPanel} = useDockedPanelActions()
  const ref = useRef<HTMLButtonElement>(null)

  return (
    <Button
      type="button"
      variant={action.variant ?? 'invisible'}
      size="small"
      onClick={() => {
        sendAnalytics({action_type: 'panel', label: action.label, panel: action.panel, ...action.analyticsPayload})
        openPanel(action.panel, {metadata: action.metadata, returnFocusTo: ref.current ?? undefined})
      }}
      ref={ref}
      aria-label={action.ariaLabel}
    >
      <ActionLabel label={action.label} count={action.count} />
    </Button>
  )
}
